import React, { useState, useEffect, useCallback, useRef } from 'react';
import './TiffinTheme.css';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import tiffinOrderService from '../services/tiffinOrderService';
import tiffinAdminService from '../services/tiffinAdminService';
import tiffinService from '../services/tiffinService';
import {
  setMenuItems,
  setMenuLoading,
  setMenuError,
  addToCart,
  removeFromCart,
  setCartItemQuantity,
  clearCart,
  setCustomerInfo,
  setOrder,
  setOrderLoading,
  setOrderError,
} from '../../../redux/slices/tiffinSlice';

const PICKUP_ADDRESS = process.env.REACT_APP_PICKUP_ADDRESS || 'Yugal Kunj - Radha Krishna Temple and Community Center, 2769 Duluth Hwy, Duluth, GA 30096';

const generatePickupSlots = () => {
  const slots = [];
  const cur = new Date();
  cur.setHours(10, 0, 0, 0);
  while (cur.getHours() < 20 || (cur.getHours() === 20 && cur.getMinutes() === 0)) {
    const h = cur.getHours();
    const m = cur.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    const dh = h % 12 || 12;
    slots.push(`${dh}:${m.toString().padStart(2, '0')} ${ampm}`);
    cur.setMinutes(cur.getMinutes() + 30);
  }
  return slots;
};

const getTomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const TiffinMenu = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { menuItems, menuLoading, menuError, menuTitle, cart, customerInfo, orderLoading, orderError } = useSelector((state) => state.tiffin);
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showCartDrawer, setShowCartDrawer] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', onConfirm: null });
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 1023px)').matches);

  // Track viewport changes (handles Chrome DevTools device emulation)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (showCartDrawer) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showCartDrawer]);

  // Minimum order amount (dynamic from portal settings)
  const [minOrderAmount, setMinOrderAmount] = useState(50);

  useEffect(() => {
    tiffinService.getPortalSetting('Tiffin_Min_Order_Amount')
      .then(data => {
        const val = parseFloat(data?.value1 || data?.Value1 || data?.setting_Value_1 || 50);
        if (!isNaN(val) && val > 0) setMinOrderAmount(val);
      })
      .catch(() => {});
  }, []);

  // Sita Rosi (old Kitchen system - Lunch/Dinner)
  const [selectedItem, setSelectedItem] = useState(null);

  const [sitaRosiOpen, setSitaRosiOpen] = useState(true);
  const [sitaRosiItems, setSitaRosiItems] = useState({ lunch: [], dinner: [] });
  const [sitaRosiLoading, setSitaRosiLoading] = useState(false);
  const [sitaRosiTab, setSitaRosiTab] = useState(() => new Date().getHours() >= 14 ? 'dinner' : 'lunch');
  const [sitaRosiError, setSitaRosiError] = useState(null);
  const tomorrowStr = getTomorrowStr();
  const [sitaRosiDate, setSitaRosiDate] = useState(tomorrowStr);
  const [srSearch, setSrSearch] = useState('');
  const sitaRosiLoadingRef = useRef(false);
  const sitaRosiDateRef = useRef(sitaRosiDate); // ref so fetchSitaRosi always reads latest date
  const sitaRosiMountedRef = useRef(false); // skip first run of date-change effect

  // Keep ref in sync with state
  useEffect(() => { sitaRosiDateRef.current = sitaRosiDate; }, [sitaRosiDate]);

  // Refetch menu when user picks a different date (skip on initial mount)
  useEffect(() => {
    if (!sitaRosiMountedRef.current) { sitaRosiMountedRef.current = true; return; }
    if (!sitaRosiOpen) return;
    sitaRosiLoadingRef.current = false; // reset guard so new fetch proceeds
    fetchSitaRosi();
  }, [sitaRosiDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSitaRosi = useCallback(async () => {
    if (sitaRosiLoadingRef.current) return;
    sitaRosiLoadingRef.current = true;
    setSitaRosiLoading(true);
    setSitaRosiError(null);
    try {
      const dateOnly = sitaRosiDateRef.current;
      const fetchMeal = async (menuTypeId) => {
        const resp = await tiffinService.getKitchenMenu(dateOnly, menuTypeId);
        return resp;
      };
      const [lunch, dinner] = await Promise.all([
        fetchMeal(1).catch(e => { setSitaRosiError(e?.response?.data?.title || e?.message || 'API error'); return []; }),
        fetchMeal(2).catch(() => []),
      ]);
      const dedup = (arr) => {
        const seen = new Set();
        return (arr || []).filter(it => {
          const key = it.Menu_Item_Id || it.menu_Item_Id || it.Kitchen_Item_Id || it.kitchen_Item_Id;
          if (key && seen.has(key)) return false;
          if (key) seen.add(key);
          return true;
        });
      };
      setSitaRosiItems({ lunch: dedup(lunch), dinner: dedup(dinner) });
    } catch (e) {
      console.error('Sita Rosi fetch failed', e);
      setSitaRosiError(e?.response?.data?.title || e?.message || 'Unknown error');
    } finally {
      sitaRosiLoadingRef.current = false;
      setSitaRosiLoading(false);
    }
  }, []); // no reactive deps — loading guard uses a ref

  // Auto-load Sita Rasoi once on mount
  useEffect(() => { fetchSitaRosi(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openSitaRosi = () => {
    const hasTodayItems = cartItems.some(c => !String(c.item.tiffinMenuItem_Id).startsWith('sr_'));
    if (hasTodayItems && cartCount > 0) {
      setConfirmDialog({
        open: true,
        message: "Your cart has Today's Special Menu items. Opening Sita Rasoi will clear Today's cart. Continue?",
        onConfirm: () => { dispatch(clearCart()); setSitaRosiOpen(true); fetchSitaRosi(); setConfirmDialog({ open: false, message: '', onConfirm: null }); }
      });
      return;
    }
    setSitaRosiOpen(true);
    fetchSitaRosi();
  };

  // Menu selector
  const [availableMenus, setAvailableMenus] = useState([]);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const pillScrollRef = useRef(null);
  const selectedPillRef = useRef(null);
  const fetchingMenuRef = useRef(false);

  // Pre-fill customer info from auth if logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(setCustomerInfo({
        firstName: user?.User_First_Name || user?.user_First_Name || user?.firstName || '',
        lastName: user?.User_Last_Name || user?.user_Last_Name || user?.lastName || '',
        phone: user?.User_Phone || user?.User_Cell_Phone_No || user?.user_Phone || '',
        email: user?.User_Email || user?.user_Email || user?.email || '',
      }));
    }
  }, [isAuthenticated, user, dispatch]);

  // Load available menus list
  useEffect(() => {
    tiffinAdminService.getMenus().then(data => {
      const sorted = (data || []).sort((a, b) => new Date(b.menuDate) - new Date(a.menuDate));
      setAvailableMenus(sorted);
      const menusWithItems = sorted.filter(m => (m.itemCount || m.ItemCount || 0) > 0);
      const defaultMenu = menusWithItems.length > 0
        ? menusWithItems.reduce((best, m) =>
            (m.itemCount || m.ItemCount || 0) > (best.itemCount || best.ItemCount || 0) ? m : best)
        : sorted[0];
      if (defaultMenu) setSelectedMenuId(defaultMenu.tiffinMenu_Id || defaultMenu.TiffinMenu_Id);
    }).catch(e => console.error('Failed to load menus list', e));
  }, []);

  // Fetch active menu by selected menu date
  const fetchMenu = useCallback(async (menuDate) => {
    if (!menuDate) return;
    if (fetchingMenuRef.current) return;
    fetchingMenuRef.current = true;
    dispatch(setMenuLoading(true));
    dispatch(setMenuError(null));
    try {
      const data = await tiffinOrderService.getActiveMenu(menuDate);
      dispatch(setMenuItems(data || []));
    } catch (err) {
      dispatch(setMenuError('Failed to load menu. Please try again later.'));
      console.error(err);
    } finally {
      dispatch(setMenuLoading(false));
      fetchingMenuRef.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    if (selectedMenuId && availableMenus.length > 0) {
      const m = availableMenus.find(x => x.tiffinMenu_Id === selectedMenuId);
      if (m) fetchMenu(m.menuDate?.split('T')[0]);
    }
  }, [selectedMenuId, availableMenus, fetchMenu]);

  // Auto-scroll selected pill into view
  useEffect(() => {
    if (selectedPillRef.current && pillScrollRef.current) {
      selectedPillRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [selectedMenuId]);

  // Cart calculations
  const cartItems = Object.values(cart);
  const cartTotal = cartItems.reduce((sum, c) => sum + ((c.item.price || 0) * c.quantity), 0);
  const cartCount = cartItems.reduce((sum, c) => sum + c.quantity, 0);

  const getItemQuantity = (itemId) => {
    return cart[itemId]?.quantity || 0;
  };

  const handleAdd = (item) => {
    const isFromSitaRosi = String(item.tiffinMenuItem_Id).startsWith('sr_');
    if (cartCount > 0) {
      const hasConflict = cartItems.some(c => String(c.item.tiffinMenuItem_Id).startsWith('sr_') !== isFromSitaRosi);
      if (hasConflict) {
        const from = isFromSitaRosi ? 'Sita Rasoi' : "Today's Special Menu";
        const other = isFromSitaRosi ? "Today's Special Menu" : 'Sita Rasoi';
        setConfirmDialog({
          open: true,
          message: `Your cart has ${other} items. Only one menu at a time is allowed. Clear cart and switch to ${from}?`,
          onConfirm: () => { dispatch(clearCart()); dispatch(addToCart(item)); setConfirmDialog({ open: false, message: '', onConfirm: null }); }
        });
        return;
      }
    }
    dispatch(addToCart(item));
  };

  const handleRemove = (itemId) => {
    dispatch(removeFromCart(itemId));
  };

  const handleQuantityChange = (itemId, qty) => {
    const num = parseInt(qty) || 0;
    dispatch(setCartItemQuantity({ id: itemId, quantity: num }));
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    dispatch(setCustomerInfo({ [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    }
  };

  const validateCustomerInfo = () => {
    const errors = {};
    const fn = (customerInfo.firstName || '').trim();
    const ln = (customerInfo.lastName || '').trim();
    const email = (customerInfo.email || '').trim();
    const rawPhone = (customerInfo.phone || '').trim();
    const digits = rawPhone.replace(/\D/g, '');

    if (!fn) errors.firstName = 'First name is required';
    else if (fn.length < 2) errors.firstName = 'At least 2 characters required';
    else if (fn.length > 50) errors.firstName = 'Max 50 characters allowed';
    else if (/[^a-zA-Z\s'\-]/.test(fn)) errors.firstName = 'Letters only – no numbers or symbols';

    if (!ln) errors.lastName = 'Last name is required';
    else if (ln.length < 2) errors.lastName = 'At least 2 characters required';
    else if (ln.length > 50) errors.lastName = 'Max 50 characters allowed';
    else if (/[^a-zA-Z\s'\-]/.test(ln)) errors.lastName = 'Letters only – no numbers or symbols';

    if (!email) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) errors.email = 'Enter a valid email address';

    if (!rawPhone) errors.phone = 'Phone number is required';
    else if (/[a-zA-Z]/.test(rawPhone)) errors.phone = 'Phone number cannot contain letters';
    else if (digits.length < 10) errors.phone = 'Must be at least 10 digits';
    else if (digits.length > 15) errors.phone = 'Phone number too long';

    if (!customerInfo.pickupTime) errors.pickupTime = 'Please select a pickup time';

    return errors;
  };

  const handleProceedToPayment = async () => {
    if (cartCount === 0) return;

    // Show customer form if not visible
    if (!showCustomerForm) {
      setShowCustomerForm(true);
      return;
    }

    // Validate
    const errors = validateCustomerInfo();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Scroll to first error field
      const firstErrorField = Object.keys(errors)[0];
      setTimeout(() => {
        document.querySelector(`[name="${firstErrorField}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    dispatch(setOrderLoading(true));
    dispatch(setOrderError(null));

    try {
      const allSitaRosi = cartItems.every(c => String(c.item.tiffinMenuItem_Id).startsWith('sr_'));
      const userId = user?.User_Id || user?.user_Id || user?.userId || null;

      let result;
      if (allSitaRosi) {
        // Sita Rosi order — save to Kitchen_Order / Kitchen_Order_Item tables
        const kitchenItems = cartItems.map(c => ({
          kitcheItemId: parseInt(String(c.item.tiffinMenuItem_Id).replace('sr_', ''), 10) || 0,
          itemName: c.item.itemName,
          quantity: c.quantity,
          price: c.item.price,
        }));
        const kitchenInput = {
          firstName: customerInfo.firstName,
          lastName: customerInfo.lastName,
          phone: customerInfo.phone,
          email: customerInfo.email,
          notes: customerInfo.notes,
          pickupTime: customerInfo.pickupTime || '',
          pickupDate: sitaRosiDate || '',
          userId,
          orgId: 3,
          items: kitchenItems,
        };
        const kitchenResult = await tiffinOrderService.createKitchenOrder(kitchenInput);
        result = {
          tiffinOrder_Id: kitchenResult.kitchen_Orders_Id || kitchenResult.Kitchen_Orders_Id,
          totalAmount: kitchenResult.totalAmount || kitchenResult.TotalAmount,
          paymentStatus: kitchenResult.paymentStatus || kitchenResult.PaymentStatus || 'Pending',
          orderType: 'kitchen',
          mealDate: sitaRosiDate,
        };
      } else {
        // Today's Special — save to TiffinOrder / TiffinOrderItem tables
        const tiffinItems = cartItems.map(c => ({
          tiffinMenuItem_Id: c.item.tiffinMenuItem_Id,
          itemName: c.item.itemName,
          quantity: c.quantity,
          price: c.item.price,
        }));
        const orderInput = {
          firstName: customerInfo.firstName,
          lastName: customerInfo.lastName,
          phone: customerInfo.phone,
          email: customerInfo.email,
          notes: customerInfo.notes,
          userId,
          orgId: 3,
          items: tiffinItems,
        };
        const tiffinResult = await tiffinOrderService.createOrder(orderInput);
        result = { ...tiffinResult, orderType: 'tiffin' };
      }

      dispatch(setOrder(result));

      // Navigate to payment page
      navigate('/tiffin/payment');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to create order. Please try again.';
      dispatch(setOrderError(msg));
      console.error(err);
    } finally {
      dispatch(setOrderLoading(false));
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'var(--yk-bg, linear-gradient(180deg, #faf8f5 0%, #f5f0eb 40%, #faf8f5 100%))'}}>
      {/* Hero Header */}
      <section className="relative overflow-hidden" style={{background: 'var(--yk-surface, linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(250,244,235,0.95) 100%))', backdropFilter: 'saturate(180%) blur(20px)', WebkitBackdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid var(--yk-border, rgba(180,160,130,0.18))', boxShadow: '0 4px 24px rgba(120,80,40,0.07)'}}>
        {/* Background decorative circles */}
        <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full pointer-events-none" style={{background: 'radial-gradient(circle, rgba(217,119,6,0.07) 0%, transparent 70%)'}}></div>
        <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full pointer-events-none" style={{background: 'radial-gradient(circle, rgba(180,83,9,0.05) 0%, transparent 70%)'}}></div>

        <div className="container-custom">
          <div className="py-6 md:py-8">
            {/* Back + two menu toggle buttons */}
            <div className="flex items-center gap-3 mb-5">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full transition-all hover:opacity-70 flex-shrink-0"
                style={{background: 'var(--yk-surface-2, rgba(120,80,40,0.08))', border: '1px solid var(--yk-border, rgba(180,160,130,0.25))', color: 'var(--yk-text-muted, #78716c)'}}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                Back
              </button>

              <div className="tiffin-menu-tab-pill flex flex-1 p-1 rounded-2xl gap-1" style={{background: '#f4f4f5', border: '1px solid #e5e7eb'}}>
                {/* Sita Rasoi button - first tab */}
                <button
                  onClick={openSitaRosi}
                  className={`flex-1 inline-flex items-center justify-center gap-2 font-semibold text-sm px-4 py-2.5 rounded-xl transition-all duration-200 ${sitaRosiOpen ? 'tiffin-menu-tab-active' : 'tiffin-menu-tab-inactive'}`}
                  style={sitaRosiOpen
                    ? {background: '#ffffff', color: '#111827', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', border: '1px solid #e5e7eb'}
                    : {background: 'transparent', color: '#71717a', border: '1px solid transparent'}}
                >
                  🏠 Sita Rasoi
                </button>

                {/* Today's Menu button - second tab */}
                <button
                  onClick={() => setSitaRosiOpen(false)}
                  className={`flex-1 inline-flex items-center justify-center gap-2 font-semibold text-sm px-4 py-2.5 rounded-xl transition-all duration-200 ${!sitaRosiOpen ? 'tiffin-menu-tab-active' : 'tiffin-menu-tab-inactive'}`}
                  style={!sitaRosiOpen
                    ? {background: '#ffffff', color: '#111827', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', border: '1px solid #e5e7eb'}
                    : {background: 'transparent', color: '#71717a', border: '1px solid transparent'}}
                >
                  🍽️ Today's Menu
                  {menuItems.length > 0 && (
                    <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{background: !sitaRosiOpen ? '#f4f4f5' : 'transparent', color: '#71717a', fontSize: '0.65rem'}}>{menuItems.length}</span>
                  )}
                </button>
              </div>
            </div>

            {/* Title */}
            <div className="flex items-center justify-between gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight" style={{background: 'linear-gradient(135deg,#92400e 0%,#b45309 50%,#d97706 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: '-0.02em'}}>
                {sitaRosiOpen ? '🏠 Sita Rasoi' : (menuTitle || "Today's Special Menu")}
              </h1>
              {sitaRosiOpen && (
                <div className="sr-search-box flex items-center gap-1.5 px-3 py-1.5 rounded-xl flex-shrink-0" style={{background: 'var(--yk-bg-subtle, #f4f4f5)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: '#78716c'}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={srSearch}
                    onChange={e => setSrSearch(e.target.value)}
                    className="sr-search-input bg-transparent outline-none border-none text-sm font-medium"
                    style={{color: 'var(--yk-text, #374151)', width: '180px'}}
                  />
                  {srSearch && (
                    <button onClick={() => setSrSearch('')} className="flex-shrink-0 text-xs transition" style={{color: '#9ca3af'}}>✕</button>
                  )}
                </div>
              )}
            </div>

            {/* Subtitle + pills on same row */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-medium flex items-center gap-2" style={{color: 'var(--yk-text-muted, #78716c)'}}>
                <span>🌿</span>
                Fresh, homemade vegetarian meals — prepared daily with love &amp; care
              </p>
              {sitaRosiOpen ? (
                <div className="flex flex-wrap items-center gap-2">
                  <div className="sr-lunch-dinner-bar flex p-1 rounded-xl gap-1" style={{background: '#f4f4f5', border: '1px solid #e5e7eb'}}>
                    {['lunch', 'dinner'].map(tab => (
                      <button key={tab} onClick={() => setSitaRosiTab(tab)}
                        className={`sr-tab${sitaRosiTab === tab ? ' sr-tab-active' : ''} px-5 py-2 rounded-lg text-sm font-semibold transition-all`}
                        style={sitaRosiTab === tab
                          ? {background: '#ffffff', color: '#111827', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', border: '1px solid #e5e7eb'}
                          : {background: 'transparent', color: '#71717a', border: '1px solid transparent'}}>
                        {tab === 'lunch' ? '☀️ Lunch' : '🌙 Dinner'}
                      </button>
                    ))}
                  </div>
                  <span className="sr-nextday-badge flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl" style={{background:'#fef9c3', color:'#854d0e', border:'1px solid #fde68a', whiteSpace:'nowrap'}}>📅 Next-day pickup only</span>
                  <div className="sr-date-picker-wrapper flex items-center gap-1.5 px-3 py-1.5 rounded-xl" style={{background: '#f4f4f5', border: '1px solid #e5e7eb'}}>
                    <svg className="sr-date-icon w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color:'#78716c'}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    <input
                      type="date"
                      value={sitaRosiDate}
                      min={tomorrowStr}
                      onChange={e => {
                        const val = e.target.value;
                        setSitaRosiDate(val && val >= tomorrowStr ? val : tomorrowStr);
                      }}
                      className="sr-date-input text-xs font-semibold bg-transparent outline-none border-none cursor-pointer"
                      style={{color: '#374151', minWidth: '110px'}}
                    />
                  </div>
                </div>
              ) : availableMenus.filter(m => (m.itemCount || m.ItemCount || 0) > 0).length > 1 && (
                <div ref={pillScrollRef} className="flex items-center gap-2 overflow-x-auto" style={{scrollbarWidth: 'none'}}>
                  {availableMenus.filter(m => (m.itemCount || m.ItemCount || 0) > 0).map(m => {
                    const isSelected = selectedMenuId === m.tiffinMenu_Id;
                    return (
                      <button
                        key={m.tiffinMenu_Id}
                        ref={isSelected ? selectedPillRef : null}
                        onClick={() => setSelectedMenuId(m.tiffinMenu_Id)}
                        className={`tiffin-menu-pill flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition-all whitespace-nowrap${isSelected ? ' tiffin-menu-pill--selected' : ''}`}
                        style={isSelected
                          ? {background: '#fff', border: '2px solid #b45309', color: '#b45309', boxShadow: '0 2px 8px rgba(180,83,9,0.15)'}
                          : {background: '#fff', color: '#78716c', border: '1px solid rgba(180,160,130,0.3)'}
                        }
                      >
                        {m.menuTitle}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Decorative bottom gradient line */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{background: 'linear-gradient(90deg, transparent 0%, rgba(180,83,9,0.25) 30%, rgba(217,119,6,0.35) 50%, rgba(180,83,9,0.25) 70%, transparent 100%)'}}></div>
      </section>

      <div className="container-custom py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* Menu Items - Left side */}
          <div className="lg:col-span-2">
            {!sitaRosiOpen ? (<>
            {menuLoading && (
              <div className="text-center py-20">
                <div className="inline-flex items-center gap-3 bg-orange-50 px-6 py-3 rounded-full">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-200 border-t-orange-600"></div>
                  <span className="text-orange-700 font-medium text-sm">Loading today's menu...</span>
                </div>
              </div>
            )}

            {menuError && (
              <div className="bg-red-50 border border-red-100 rounded-2xl p-8 text-center">
                <div className="text-3xl mb-3">⚠️</div>
                <p className="text-red-700 font-semibold mb-4">{menuError}</p>
                <button onClick={fetchMenu} className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-8 rounded-full transition shadow-sm">
                  Try Again
                </button>
              </div>
            )}

            {!menuLoading && !menuError && menuItems.length === 0 && (
              <div className="bg-gray-50 rounded-2xl p-16 text-center border border-gray-100">
                <div className="text-6xl mb-5">🍽️</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">No Menu Available</h3>
                <p className="text-gray-500 leading-relaxed max-w-md mx-auto">
                  Today's menu hasn't been published yet. Please check back later.
                </p>
              </div>
            )}

            {!menuLoading && menuItems.length > 0 && (
              <div className="space-y-3">
                {menuItems.map((item, index) => {
                  const qty = getItemQuantity(item.tiffinMenuItem_Id);
                  return (
                    <div
                      key={item.tiffinMenuItem_Id}
                      className={`tiffin-item-card group relative rounded-2xl p-5 transition-all duration-300 hover:scale-[1.01]${index === 0 ? ' mt-3' : ''}`}
                      style={{
                        background: qty > 0 ? 'var(--yk-surface, rgba(255,255,255,0.95))' : 'var(--yk-surface, rgba(255,255,255,0.75))',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: qty > 0 ? '1px solid var(--yk-border, rgba(180,130,60,0.25))' : '1px solid var(--yk-border, rgba(180,160,130,0.12))',
                        boxShadow: qty > 0
                          ? '0 10px 30px rgba(120, 80, 40, 0.1), 0 2px 4px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.8)'
                          : '0 2px 12px rgba(120, 80, 40, 0.04), 0 1px 2px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.6)',
                      }}
                    >
                      {/* Item number badge */}
                      <div className="absolute -top-2.5 -left-2.5 w-7 h-7 text-[10px] font-bold rounded-full flex items-center justify-center" style={{background: 'var(--yk-surface-2, linear-gradient(135deg, #fdf6ee, #f5e6d0))', color: 'var(--yk-text, #6b4c0a)', border: '1px solid var(--yk-border, rgba(200,170,110,0.35))', boxShadow: '0 3px 10px rgba(160, 130, 70, 0.12), inset 0 1px 0 rgba(255,255,255,0.7)'}}>
                        {index + 1}
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        {/* Item Image */}
                        {item.imageUrl && (
                          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0" style={{border: '1px solid rgba(180,160,130,0.15)', boxShadow: '0 3px 10px rgba(120,80,40,0.08), inset 0 0 0 1px rgba(255,255,255,0.1)'}}>
                            <img src={item.imageUrl} alt={item.itemName} className="w-full h-full object-cover" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold truncate" style={{color: 'var(--yk-text, #292524)'}}>{item.itemName}</h3>
                            {qty > 0 && (
                              <span className="flex-shrink-0 w-5 h-5 text-[10px] font-bold rounded-full flex items-center justify-center" style={{background: 'var(--yk-surface-2, linear-gradient(135deg, #fdf6ee, #f5e6d0))', color: 'var(--yk-text, #6b4c0a)', border: '1px solid var(--yk-border, rgba(200,170,110,0.35))', boxShadow: '0 2px 6px rgba(160, 130, 70, 0.1)'}}>
                                {qty}
                              </span>
                            )}
                          </div>
                          {item.itemDescription && (
                            <p className="text-sm mt-0.5 leading-relaxed truncate" style={{color: '#a8a29e'}}>{item.itemDescription}</p>
                          )}
                          <p className="text-lg font-bold mt-1.5" style={{color: 'var(--yk-primary, #92400e)'}}>${(item.price || 0).toFixed(2)}</p>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => setSelectedItem({ id: item.tiffinMenuItem_Id, name: item.itemName, description: item.itemDescription || '', price: item.price || 0, imageUrl: item.imageUrl || '', itemObj: item })}
                            className="tiffin-view-detail-btn w-8 h-8 rounded-full flex items-center justify-center transition hover:scale-110 flex-shrink-0"
                            style={{background: 'var(--yk-surface-2, #f4f4f5)', border: '1px solid var(--yk-border, #e5e7eb)', color: 'var(--yk-text-muted, #78716c)'}}
                            title="View details"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                          </button>
                          {qty === 0 ? (
                            <button
                              onClick={() => handleAdd(item)}
                              className="tiffin-add-btn font-semibold py-2.5 px-6 rounded-xl transition-all duration-200 text-sm hover:scale-105 active:scale-95"
                              style={{background: '#ffffff', color: '#374151', border: '1px solid #d1d5db', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'}}
                            >
                              Add +
                            </button>
                          ) : (
                            <div className="tiffin-stepper flex items-center gap-1 rounded-xl px-1 py-1" style={{background: 'var(--yk-surface-2, #f4f4f5)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
                              <button
                                onClick={() => handleRemove(item.tiffinMenuItem_Id)}
                                className="tiffin-stepper-minus-main tiffin-stepper-btn w-9 h-9 rounded-lg font-bold flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition text-base"
                                style={{background: '#ffffff', border: '1px solid #e5e7eb', color: '#374151'}}
                              >
                                −
                              </button>
                              <input
                                type="number"
                                min="0"
                                value={qty}
                                onChange={(e) => handleQuantityChange(item.tiffinMenuItem_Id, e.target.value)}
                                className="tiffin-stepper-qty w-10 text-center bg-transparent font-bold text-gray-800 text-sm border-none outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                              <button
                                onClick={() => handleAdd(item)}
                                className="tiffin-stepper-add tiffin-stepper-btn w-9 h-9 rounded-lg font-bold flex items-center justify-center transition text-base hover:scale-105 active:scale-95"
                                style={{background: '#ffffff', color: '#111827', border: '1px solid #d1d5db'}}
                              >
                                +
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </>) : (
              <div className="space-y-5">
                {/* Loading */}
                {sitaRosiLoading && (
                  <div className="text-center py-20">
                    <div className="inline-flex items-center gap-3 bg-orange-50 px-6 py-3 rounded-full">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-200 border-t-orange-600"></div>
                      <span className="text-orange-700 font-medium text-sm">Loading kitchen menu...</span>
                    </div>
                  </div>
                )}
                {/* Error */}
                {sitaRosiError && !sitaRosiLoading && (
                  <div className="bg-red-50 border border-red-100 rounded-2xl p-8 text-center">
                    <p className="text-red-700 font-semibold mb-3">{sitaRosiError}</p>
                    <button onClick={fetchSitaRosi} className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-6 rounded-full text-sm transition">Retry</button>
                  </div>
                )}
                {/* Items */}
                {!sitaRosiLoading && (() => {
                  const srItems = (sitaRosiItems[sitaRosiTab] || []).filter(it =>
                    !srSearch.trim() || (it.item_Name || it.Item_Name || '').toLowerCase().includes(srSearch.toLowerCase().trim())
                  );
                  if (srItems.length === 0 && !sitaRosiError) return (
                    <div className="bg-gray-50 rounded-2xl p-16 text-center border border-gray-100">
                      <div className="text-5xl mb-4">{sitaRosiTab === 'lunch' ? '☀️' : '🌙'}</div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">No {sitaRosiTab} items today</h3>
                      <p className="text-gray-500 text-sm">Please check back later.</p>
                    </div>
                  );
                  const srGrouped = srItems.reduce((acc, it) => {
                    const cat = it.item_Category_Name || it.Item_Category_Name || 'Other';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(it);
                    return acc;
                  }, {});
                  return (
                    <div className="space-y-6">
                      {Object.entries(srGrouped).map(([cat, catItems]) => (
                        <div key={cat}>
                          <div className="mb-3">
                            <span className="sr-category text-xs font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md"
                              style={{background: '#f4f4f5', color: '#52525b', border: '1px solid #e5e7eb'}}>
                              {cat}
                            </span>
                          </div>
                          <div className="space-y-3">
                            {catItems.map((it, idx) => {
                              const rawUrl = it.item_Media_URL || it.Item_Media_URL || '';
                              const imgSrc = rawUrl
                                ? rawUrl.startsWith('http') ? rawUrl
                                  : `https://ykportal.yugalkunj.org/${rawUrl.replace(/^\.\.\//, '')}`
                                : '';
                              const isPtr = (it.is_Plate_Or_Tray || it.Is_Plate_Or_Tray) === 'Y';
                              const hpP = parseFloat(it.half_Plate_Price || it.Half_Plate_Price || 0);
                              const fpP = parseFloat(it.full_Plate_Price || it.Full_Plate_Price || 0);
                              const htP = parseFloat(it.half_Tray_Price  || it.Half_Tray_Price  || 0);
                              const ftP = parseFloat(it.full_Tray_Price  || it.Full_Tray_Price  || 0);
                              const htC = it.half_Tray_Count || it.Half_Tray_Count || '';
                              const ftC = it.full_Tray_Count || it.Full_Tray_Count || '';
                              const unitP = parseFloat(it.pricePerUnit || it.PricePerUnit || 0);
                              const baseP = isPtr ? (hpP || fpP || htP || ftP) : (unitP || hpP || 0);
                              const bId = `sr_${it.kitchen_Item_Id || it.Kitchen_Item_Id || it.menu_Item_Id || it.Menu_Item_Id || idx}`;
                              const hpId = `${bId}_hp`; const fpId = `${bId}_fp`;
                              const htId = `${bId}_ht`; const ftId = `${bId}_ft`;
                              const qhp = getItemQuantity(hpId); const qfp = getItemQuantity(fpId);
                              const qht = getItemQuantity(htId); const qft = getItemQuantity(ftId);
                              const qU  = getItemQuantity(bId);
                              const inC = isPtr ? (qhp > 0 || qfp > 0 || qht > 0 || qft > 0) : qU > 0;
                              const mkIt = (id, label, price) => ({
                                tiffinMenuItem_Id: id,
                                itemName: label ? `${it.item_Name || it.Item_Name} (${label})` : (it.item_Name || it.Item_Name),
                                itemDescription: it.item_Description || it.Item_Description || '',
                                price, imageUrl: imgSrc,
                              });
                              const vBtn = (id, label, price, qty) => price > 0 ? (
                                qty === 0 ? (
                                  <button key={id} onClick={() => handleAdd(mkIt(id, label, price))}
                                    className="sr-add-btn font-medium py-1.5 px-2.5 rounded-lg text-xs transition-all hover:scale-[1.02] active:scale-95 w-full max-w-[280px] flex items-center justify-between gap-2"
                                    style={{
                                      background: 'var(--yk-surface, #ffffff)',
                                      color: 'var(--yk-text, #374151)',
                                      border: '1px solid var(--yk-border, #d1d5db)',
                                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                                    }}>
                                    <div className="flex-1 min-w-0 text-left">
                                      <div className="text-xs font-medium leading-tight">{label}</div>
                                      <div className="text-xs font-bold mt-0.5" style={{color: 'var(--yk-primary, #92400e)'}}>${price.toFixed(2)}</div>
                                    </div>
                                    <span className="font-bold flex-shrink-0" style={{color: 'var(--yk-text-muted, #9ca3af)'}}>+</span>
                                  </button>
                                ) : (
                                  <div key={id} className="sr-tray-active flex flex-col gap-1 w-full max-w-[280px] px-2.5 py-1.5 rounded-lg"
                                    style={{
                                      background: 'var(--yk-primary-bg, rgba(120,80,40,0.06))',
                                      border: '1px solid var(--yk-primary-border, rgba(120,80,40,0.18))'
                                    }}>
                                    <span className="text-xs font-semibold leading-tight" style={{color: 'var(--yk-text, #374151)'}}>{label}</span>
                                    <div className="flex items-center gap-2">
                                      <div className="sr-stepper flex items-center gap-0.5 rounded-md px-0.5"
                                        style={{background: 'var(--yk-bg-subtle, #f4f4f5)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
                                        <button onClick={() => handleRemove(id)} className="sr-stepper-btn sr-stepper-minus w-6 h-6 rounded font-bold flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition text-xs"
                                          style={{background: 'var(--yk-surface, #ffffff)', border: '1px solid var(--yk-border, #e5e7eb)', color: 'var(--yk-text, #374151)'}}>−</button>
                                        <span className="sr-qty text-xs font-bold w-4 text-center" style={{color: 'var(--yk-text, #111827)'}}>{qty}</span>
                                        <button onClick={() => handleAdd(mkIt(id, label, price))} className="sr-stepper-btn sr-stepper-add w-6 h-6 rounded font-bold flex items-center justify-center transition text-xs hover:scale-105"
                                          style={{background: 'var(--yk-surface, #ffffff)', color: 'var(--yk-text, #111827)', border: '1px solid var(--yk-border, #d1d5db)'}}>+</button>
                                      </div>
                                      <span className="text-xs font-bold" style={{color: 'var(--yk-primary, #92400e)'}}>${price.toFixed(2)}</span>
                                    </div>
                                  </div>
                                )
                              ) : null;
                              return (
                                <div key={idx} className={`sr-item-card${inC ? ' is-in-cart' : ''} group relative rounded-2xl p-6 transition-all duration-300 hover:scale-[1.01]`}
                                  style={{
                                    background: inC ? 'var(--yk-surface, rgba(255,255,255,0.95))' : 'var(--yk-surface, rgba(255,255,255,0.75))',
                                    backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                                    border: inC ? '1px solid var(--yk-border, rgba(180,130,60,0.25))' : '1px solid var(--yk-border, rgba(180,160,130,0.12))',
                                    boxShadow: inC ? '0 10px 30px rgba(120,80,40,0.1),inset 0 1px 0 rgba(255,255,255,0.8)' : '0 2px 12px rgba(120,80,40,0.04),inset 0 1px 0 rgba(255,255,255,0.6)',
                                  }}>
                                  <div className="flex items-stretch gap-4">
                                    <div className="flex-1 min-w-0 flex flex-col">
                                      <h3 className="sr-item-name text-base font-bold" style={{color: 'var(--yk-text, #1c1917)'}}>{it.item_Name || it.Item_Name}</h3>
                                      {(it.item_Description || it.Item_Description) && (
                                        <p className="sr-item-desc text-sm mt-1.5 leading-relaxed" style={{color: 'var(--yk-text-secondary, #44403c)'}}>{it.item_Description || it.Item_Description}</p>
                                      )}
                                      {!isPtr && baseP > 0 && (
                                        <p className="sr-price text-lg font-bold mt-2" style={{color: 'var(--yk-primary, #92400e)'}}>${baseP.toFixed(2)}</p>
                                      )}
                                      <div className="flex flex-col gap-2 mt-3">
                                        {isPtr ? (
                                          <>
                                            {vBtn(htId, htC ? `Half Tray (${htC})` : 'Half Tray', htP, qht)}
                                            {vBtn(ftId, ftC ? `Full Tray (${ftC})` : 'Full Tray', ftP, qft)}
                                          </>
                                        ) : (
                                          qU === 0 ? (
                                            <button onClick={() => handleAdd(mkIt(bId, '', baseP))}
                                              className="sr-add-btn font-semibold py-1.5 px-5 rounded-xl text-sm transition-all hover:scale-105 active:scale-95 self-start"
                                              style={{background: 'var(--yk-surface, #ffffff)', color: 'var(--yk-text, #374151)', border: '1px solid var(--yk-border, #d1d5db)', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'}}>
                                              Add +
                                            </button>
                                          ) : (
                                            <div className="sr-stepper flex items-center gap-1 rounded-xl px-1 py-1 self-start"
                                              style={{background: 'var(--yk-bg-subtle, #f4f4f5)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
                                              <button onClick={() => handleRemove(bId)} className="sr-stepper-btn sr-stepper-minus w-8 h-8 rounded-lg font-bold flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition"
                                                style={{background: 'var(--yk-surface, #ffffff)', border: '1px solid var(--yk-border, #e5e7eb)', color: 'var(--yk-text, #374151)'}}>−</button>
                                              <span className="sr-qty text-sm font-bold w-6 text-center" style={{color: 'var(--yk-text, #111827)'}}>{qU}</span>
                                              <button onClick={() => handleAdd(mkIt(bId, '', baseP))} className="sr-stepper-btn sr-stepper-add w-8 h-8 rounded-lg font-bold flex items-center justify-center transition hover:scale-105"
                                                style={{background: 'var(--yk-surface, #ffffff)', color: 'var(--yk-text, #111827)', border: '1px solid var(--yk-border, #d1d5db)'}}>+</button>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                    {imgSrc && (
                                      <div className="sr-img-container relative flex-shrink-0 rounded-xl overflow-hidden group/img w-40 lg:w-60"
                                        style={{minHeight: '120px', border: '1px solid var(--yk-border, rgba(180,160,130,0.2))', boxShadow: '0 4px 20px rgba(120,80,40,0.15)', background: 'var(--yk-bg-subtle, #f5f5f4)'}}>
                                        <img src={imgSrc} alt={it.item_Name || it.Item_Name}
                                          onError={e => { e.target.parentElement.style.display = 'none'; }}
                                          className="absolute inset-0 w-full h-full object-contain transition-transform duration-300 group-hover/img:scale-105" />
                                        <a href={imgSrc} target="_blank" rel="noopener noreferrer"
                                          className="absolute inset-0 flex items-end justify-center pb-2 opacity-0 group-hover/img:opacity-100 transition-opacity duration-200"
                                          style={{background: 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 55%)'}}
                                          onClick={e => e.stopPropagation()}>
                                          <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{background: 'rgba(255,255,255,0.92)', color: '#1c1917', fontSize: '0.7rem'}}>🔍 View</span>
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      <button onClick={fetchSitaRosi} disabled={sitaRosiLoading}
                        className="w-full mt-2 py-2.5 rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
                        style={{background: 'rgba(180,83,9,0.05)', color: '#b45309', border: '1px solid rgba(180,83,9,0.12)'}}>
                        {sitaRosiLoading ? 'Refreshing…' : '↻ Refresh Menu'}
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Cart / Order Summary - Right side (desktop only) */}
          <div className="lg:col-span-1 hidden lg:block">
            <div className="sticky top-6">
              <div className="tiffin-cart rounded-2xl overflow-hidden mt-3" style={{background: 'var(--yk-surface, rgba(255,255,255,0.8))', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid var(--yk-border, rgba(180,160,130,0.12))', boxShadow: '0 10px 40px rgba(120, 80, 40, 0.07), 0 2px 6px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.5)'}}>
                {/* Cart header */}
                <div className="px-6 py-5" style={{borderBottom: '1px solid rgba(180,160,130,0.1)'}}>
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold flex items-center gap-2.5" style={{color: 'var(--yk-text, #44403c)'}}>
                      Your Order
                    </h2>
                    {cartCount > 0 && (
                      <span className="text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center" style={{background: 'var(--yk-surface-2, linear-gradient(135deg, #fdf6ee, #f5e6d0))', color: 'var(--yk-text, #6b4c0a)', border: '1px solid var(--yk-border, rgba(200,170,110,0.35))', boxShadow: '0 2px 6px rgba(160, 130, 70, 0.1)'}}>
                        {cartCount}
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-6">
                  {cartCount === 0 ? (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{background: 'rgba(250,245,238,0.6)', border: '1px solid rgba(180,160,130,0.1)', boxShadow: '0 4px 12px rgba(120, 80, 40, 0.04)'}}>
                        <span className="text-3xl">🍱</span>
                      </div>
                      <p className="text-sm font-medium" style={{color: 'var(--yk-text-muted, #78716c)'}}>Your cart is empty</p>
                      <p className="text-xs mt-1" style={{color: 'var(--yk-text-muted, #a8a29e)'}}>Add items from the menu to get started</p>
                    </div>
                  ) : (
                    <>
                      {/* Cart items */}
                      <div className="space-y-3 mb-5 max-h-72 overflow-y-auto pr-1">
                        {cartItems.map((c) => (
                          <div key={c.item.tiffinMenuItem_Id} className="tiffin-cart-item flex items-start justify-between gap-3 group/cartitem p-2.5 rounded-xl transition-colors"
                            style={{border: '1px solid var(--yk-border, rgba(180,160,130,0.1))'}}>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm leading-snug" style={{color: 'var(--yk-text, #111827)'}}>{c.item.itemName}</p>
                              <p className="text-xs mt-0.5 font-medium" style={{color: 'var(--yk-text-muted, #78716c)'}}>${(c.item.price || 0).toFixed(2)} × {c.quantity}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <p className="font-bold text-sm" style={{color: 'var(--yk-primary, #92400e)'}}>${((c.item.price || 0) * c.quantity).toFixed(2)}</p>
                              <button
                                onClick={() => dispatch(setCartItemQuantity({ id: c.item.tiffinMenuItem_Id, quantity: 0 }))}
                                title="Remove item"
                                className="w-5 h-5 rounded-full flex items-center justify-center transition-all opacity-0 group-hover/cartitem:opacity-100"
                                style={{color: '#d1d5db', background: 'transparent'}}
                                onMouseEnter={e => { e.currentTarget.style.color='#ef4444'; e.currentTarget.style.background='#fef2f2'; }}
                                onMouseLeave={e => { e.currentTarget.style.color='#d1d5db'; e.currentTarget.style.background='transparent'; }}
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Total */}
                      <div className="tiffin-cart-total pt-3 mb-4 px-3 py-3 rounded-xl" style={{background: 'var(--yk-bg-subtle, #f5f5f4)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
                        <div className="flex items-center justify-between">
                          <span className="text-base font-bold" style={{color: 'var(--yk-text, #111827)'}}>Total</span>
                          <span className="text-2xl font-extrabold" style={{color: 'var(--yk-primary, #92400e)'}}>${cartTotal.toFixed(2)}</span>
                        </div>
                      </div>

                      {/* Customer Info Form */}
                      {showCustomerForm && (
                        <div className="space-y-3 mb-5 border-t border-gray-100 pt-5">
                          <h3 className="font-bold text-sm flex items-center gap-2" style={{color: 'var(--yk-text, #111827)'}}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{color: 'var(--yk-text-muted, #9ca3af)'}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                            Your Details
                          </h3>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <input
                                type="text" name="firstName" placeholder="First Name *"
                                value={customerInfo.firstName} onChange={handleCustomerChange}
                                className={`w-full px-3 py-2.5 text-sm rounded-lg border ${validationErrors.firstName ? 'border-red-300 bg-red-50/50' : 'border-gray-200'} focus:border-gray-400 focus:ring-0 outline-none transition bg-gray-50`}
                              />
                              {validationErrors.firstName && <p className="text-red-500 text-xs mt-1">{validationErrors.firstName}</p>}
                            </div>
                            <div>
                              <input
                                type="text" name="lastName" placeholder="Last Name *"
                                value={customerInfo.lastName} onChange={handleCustomerChange}
                                className={`w-full px-3 py-2.5 text-sm rounded-lg border ${validationErrors.lastName ? 'border-red-300 bg-red-50/50' : 'border-gray-200'} focus:border-gray-400 focus:ring-0 outline-none transition bg-gray-50`}
                              />
                              {validationErrors.lastName && <p className="text-red-500 text-xs mt-1">{validationErrors.lastName}</p>}
                            </div>
                          </div>
                          <div>
                            <input
                              type="email" name="email" placeholder="Email *"
                              value={customerInfo.email} onChange={handleCustomerChange}
                              className={`w-full px-3 py-2.5 text-sm rounded-lg border ${validationErrors.email ? 'border-red-300 bg-red-50/50' : 'border-gray-200'} focus:border-gray-400 focus:ring-0 outline-none transition bg-gray-50`}
                            />
                            {validationErrors.email && <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>}
                          </div>
                          <input
                            type="tel" name="phone" placeholder="Phone * (for WhatsApp)"
                            value={customerInfo.phone} onChange={handleCustomerChange}
                            className={`w-full px-3 py-2.5 text-sm rounded-lg border ${validationErrors.phone ? 'border-red-300 bg-red-50/50' : 'border-gray-200'} focus:border-gray-400 focus:ring-0 outline-none transition bg-gray-50`}
                          />
                          {validationErrors.phone && <p className="text-red-500 text-xs mt-1">{validationErrors.phone}</p>}
                          <textarea
                            name="notes" placeholder="Special instructions (optional)"
                            value={customerInfo.notes} onChange={handleCustomerChange}
                            rows={2}
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-gray-400 focus:ring-0 outline-none transition resize-none bg-gray-50"
                          />
                          <div>
                            <label className="block text-xs font-semibold mb-1" style={{color: 'var(--yk-text, #6b4c0a)'}}>🕐 Pickup Time *</label>
                            <select
                              name="pickupTime"
                              value={customerInfo.pickupTime}
                              onChange={handleCustomerChange}
                              className={`w-full px-3 py-2.5 text-sm rounded-lg border ${validationErrors.pickupTime ? 'border-red-400 bg-red-50/50' : 'border-gray-200'} focus:border-gray-400 outline-none transition bg-gray-50`}
                            >
                              <option value="">Select pickup time...</option>
                              {generatePickupSlots().map(slot => (
                                <option key={slot} value={slot}>{slot}</option>
                              ))}
                            </select>
                            {validationErrors.pickupTime && <p className="text-red-500 text-xs mt-1">{validationErrors.pickupTime}</p>}
                            <p className="text-xs mt-1" style={{color: '#a8a29e'}}>📍 {PICKUP_ADDRESS}</p>
                          </div>
                        </div>
                      )}

                      {/* Error */}
                      {orderError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                          <p className="text-red-700 text-sm">{orderError}</p>
                        </div>
                      )}

                      {/* Min order warning */}
                      {cartTotal < minOrderAmount && (
                        <div className="tiffin-min-order-warn flex items-center gap-2 px-3 py-2.5 mb-3 rounded-xl">
                          <span style={{fontSize: '15px'}}>⚠️</span>
                          <span className="tiffin-min-order-warn-text text-xs font-semibold">
                            Minimum order is ${minOrderAmount.toFixed(2)}. Add ${(minOrderAmount - cartTotal).toFixed(2)} more to proceed.
                          </span>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="space-y-2.5">
                        <button
                          onClick={handleProceedToPayment}
                          disabled={orderLoading || cartTotal < minOrderAmount}
                          className="tiffin-checkout-btn w-full font-bold py-3.5 px-4 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm tracking-wide hover:scale-[1.02] active:scale-[0.98]"
                          style={{background: 'var(--yk-surface-2, #f5f5f5)', color: 'var(--yk-text, #1c1917)', border: '1.5px solid var(--yk-border, rgba(0,0,0,0.1))', boxShadow: '0 4px 16px rgba(0,0,0,0.08)}'}}
                        >
                          {orderLoading ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-amber-400 border-t-white"></div>
                              Creating Order...
                            </span>
                          ) : showCustomerForm ? (
                            `Pay $${cartTotal.toFixed(2)} →`
                          ) : (
                            'Continue to Checkout →'
                          )}
                        </button>

                        <button
                          onClick={() => dispatch(clearCart())}
                          className="w-full text-gray-400 hover:text-red-500 font-medium py-2 text-xs transition tracking-wide uppercase"
                        >
                          Clear Cart
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Mobile: floating cart button ─── */}
      {cartCount > 0 && isMobile && (
        <div className="tiffin-view-order-wrapper" style={{position:'fixed', bottom:0, left:0, right:0, zIndex:9999, padding:'12px 80px 28px 16px', background:'linear-gradient(to top, rgba(245,235,220,0.98) 60%, transparent 100%)'}}>
          <button
            onClick={() => setShowCartDrawer(true)}
            className="tiffin-view-order-btn"
            style={{width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px', borderRadius:'18px', border:'none', cursor:'pointer', background:'linear-gradient(135deg,#1c1917 0%,#292524 100%)', color:'#ffffff', fontWeight:'700', fontSize:'16px', boxShadow:'0 4px 20px rgba(0,0,0,0.25), 0 1px 4px rgba(0,0,0,0.15)'}}>
            <span style={{minWidth:28,height:28,borderRadius:'50%',background:'rgba(255,255,255,0.15)',border:'1.5px solid rgba(255,255,255,0.25)',color:'#ffffff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800,padding:'0 6px'}}>{cartCount}</span>
            <span style={{letterSpacing:'-0.01em'}}>View Order</span>
            <span style={{fontWeight:800,fontSize:'17px',letterSpacing:'-0.02em'}}>${cartTotal.toFixed(2)}</span>
          </button>
        </div>
      )}

      {/* ── Mobile cart drawer ─── */}
      {showCartDrawer && (
        <div style={{position:'fixed', inset:0, zIndex:10000, display:'flex', flexDirection:'column', justifyContent:'flex-end'}}>
          {/* Backdrop */}
          <div style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.5)'}} onClick={() => setShowCartDrawer(false)} />
          {/* Sheet */}
          <div className="tiffin-mobile-cart-panel relative flex flex-col rounded-t-3xl" style={{maxHeight: '92vh', boxShadow: '0 -16px 60px rgba(0,0,0,0.18)', background: 'var(--yk-bg, #fffdf9)'}}>
            {/* drag handle */}
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-12 h-1.5 rounded-full" style={{background: 'rgba(180,130,60,0.25)'}} />
            </div>
            {/* Header */}
            <div className="flex-shrink-0 px-6 pt-3 pb-4" style={{borderBottom: '1px solid rgba(180,140,60,0.12)'}}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-extrabold tracking-tight" style={{color: 'var(--yk-text, #292524)', letterSpacing: '-0.03em'}}>Your Order</h2>
                  <p className="text-sm font-medium mt-0.5" style={{color: '#b45309'}}>{cartCount} item{cartCount > 1 ? 's' : ''} · ${cartTotal.toFixed(2)}</p>
                </div>
                <button onClick={() => setShowCartDrawer(false)}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-lg font-light"
                  style={{background: 'rgba(180,140,60,0.1)', color: '#78716c', border: '1px solid rgba(180,140,60,0.15)'}}>
                  ×
                </button>
              </div>
            </div>
            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {/* Order items */}
              <div className="space-y-3 mb-5">
                {cartItems.map((c) => (
                  <div key={c.item.tiffinMenuItem_Id} className="tiffin-drawer-item flex items-start gap-3 p-3 rounded-2xl" style={{background: 'var(--yk-surface, #fff)', border: '1px solid var(--yk-border, rgba(180,140,60,0.1))', boxShadow: '0 2px 8px rgba(120,80,40,0.05)'}}>
                    {c.item.imageUrl && (
                      <img src={c.item.imageUrl} alt={c.item.itemName} onError={e => { e.target.style.display='none'; }}
                        className="w-12 h-12 rounded-xl object-cover flex-shrink-0 mt-0.5" style={{border: '1px solid rgba(180,140,60,0.12)'}} />
                    )}
                    <div className="flex-1 min-w-0">
                      {/* Row 1: name + delete */}
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-bold text-sm leading-snug" style={{color: 'var(--yk-text, #1c1917)'}}>{c.item.itemName}</p>
                        <button
                          onClick={() => dispatch(setCartItemQuantity({ id: c.item.tiffinMenuItem_Id, quantity: 0 }))}
                          title="Remove"
                          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition"
                          style={{color: '#d1d5db', background: 'transparent'}}
                          onMouseEnter={e => { e.currentTarget.style.color='#ef4444'; e.currentTarget.style.background='#fef2f2'; }}
                          onMouseLeave={e => { e.currentTarget.style.color='#d1d5db'; e.currentTarget.style.background='transparent'; }}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                      {/* Row 2: stepper + total */}
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="tiffin-drawer-stepper flex items-center gap-1 rounded-lg px-0.5" style={{background: '#f3f4f6', border: '1px solid #e5e7eb'}}>
                          <button onClick={() => dispatch(removeFromCart(c.item.tiffinMenuItem_Id))}
                            className="tiffin-cart-remove-btn w-7 h-7 rounded-md flex items-center justify-center font-bold text-sm hover:bg-red-50 hover:text-red-500 transition"
                            style={{background: '#ffffff', border: '1px solid #d1d5db', color: '#374151'}}>−</button>
                          <span className="tiffin-drawer-qty text-sm font-extrabold w-5 text-center" style={{color: 'var(--yk-text, #292524)'}}>{c.quantity}</span>
                          <button onClick={() => dispatch(addToCart(c.item))}
                            className="tiffin-cart-add-btn w-7 h-7 rounded-md flex items-center justify-center font-bold text-sm transition hover:scale-110"
                            style={{background: '#ffffff', color: '#111827', border: '1px solid #d1d5db'}}>+</button>
                        </div>
                        <span className="text-sm font-extrabold" style={{color: 'var(--yk-primary, #92400e)'}}>${((c.item.price || 0) * c.quantity).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Total */}
              <div className="flex items-center justify-between px-4 py-3.5 rounded-2xl mb-5" style={{background: 'var(--yk-surface-2, #f3f4f6)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
                <span className="text-base font-bold" style={{color: 'var(--yk-text, #111827)'}}>Total</span>
                <span className="text-2xl font-extrabold" style={{color: '#047857', letterSpacing: '-0.02em'}}>${cartTotal.toFixed(2)}</span>
              </div>
              {showCustomerForm && (
                <div className="space-y-3 mb-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-px flex-1" style={{background: 'rgba(180,140,60,0.15)'}} />
                    <span className="text-xs font-bold uppercase tracking-widest px-3" style={{color: '#b45309'}}>Your Details</span>
                    <div className="h-px flex-1" style={{background: 'rgba(180,140,60,0.15)'}} />
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    <div>
                      <input type="text" name="firstName" placeholder="First Name *"
                        value={customerInfo.firstName} onChange={handleCustomerChange}
                        className="w-full px-4 py-3 text-sm rounded-xl outline-none font-medium"
                        style={{background: 'var(--yk-surface, #fff)', border: validationErrors.firstName ? '1.5px solid #f87171' : '1.5px solid var(--yk-border, rgba(180,140,60,0.2))', color: 'var(--yk-text, #292524)'}} />
                      {validationErrors.firstName && <p className="text-red-500 text-xs mt-1 font-medium">{validationErrors.firstName}</p>}
                    </div>
                    <div>
                      <input type="text" name="lastName" placeholder="Last Name *"
                        value={customerInfo.lastName} onChange={handleCustomerChange}
                        className="w-full px-4 py-3 text-sm rounded-xl outline-none font-medium"
                        style={{background: 'var(--yk-surface, #fff)', border: validationErrors.lastName ? '1.5px solid #f87171' : '1.5px solid var(--yk-border, rgba(180,140,60,0.2))', color: 'var(--yk-text, #292524)'}} />
                      {validationErrors.lastName && <p className="text-red-500 text-xs mt-1 font-medium">{validationErrors.lastName}</p>}
                    </div>
                  </div>
                  <div>
                    <input type="email" name="email" placeholder="Email address *"
                      value={customerInfo.email} onChange={handleCustomerChange}
                      className="w-full px-4 py-3 text-sm rounded-xl outline-none font-medium"
                      style={{background: 'var(--yk-surface, #fff)', border: validationErrors.email ? '1.5px solid #f87171' : '1.5px solid var(--yk-border, rgba(180,140,60,0.2))', color: 'var(--yk-text, #292524)'}} />
                    {validationErrors.email && <p className="text-red-500 text-xs mt-1 font-medium">{validationErrors.email}</p>}
                  </div>
                  <div>
                    <input type="tel" name="phone" placeholder="Phone number * (10 digits)"
                      value={customerInfo.phone} onChange={handleCustomerChange}
                      className="w-full px-4 py-3 text-sm rounded-xl outline-none font-medium"
                      style={{background: 'var(--yk-surface, #fff)', border: validationErrors.phone ? '1.5px solid #f87171' : '1.5px solid var(--yk-border, rgba(180,140,60,0.2))', color: 'var(--yk-text, #292524)'}} />
                    {validationErrors.phone && <p className="text-red-500 text-xs mt-1 font-medium">{validationErrors.phone}</p>}
                  </div>
                  <textarea name="notes" placeholder="Special instructions (optional)"
                    value={customerInfo.notes} onChange={handleCustomerChange} rows={2}
                    className="w-full px-4 py-3 text-sm rounded-xl outline-none resize-none font-medium"
                    style={{background: 'var(--yk-surface, #fff)', border: '1.5px solid var(--yk-border, rgba(180,140,60,0.2))', color: 'var(--yk-text, #292524)'}} />
                  <div>
                    <label className="block text-xs font-bold mb-1.5 uppercase tracking-wide" style={{color: '#b45309'}}>🕐 Pickup Time *</label>
                    <select name="pickupTime" value={customerInfo.pickupTime} onChange={handleCustomerChange}
                      className="w-full px-4 py-3 text-sm rounded-xl outline-none font-medium"
                      style={{background: 'var(--yk-surface, #fff)', border: validationErrors.pickupTime ? '1.5px solid #f87171' : '1.5px solid var(--yk-border, rgba(180,140,60,0.2))', color: customerInfo.pickupTime ? 'var(--yk-text, #292524)' : 'var(--yk-text-muted, #a8a29e)'}}>
                      <option value="">Select pickup time…</option>
                      {generatePickupSlots().map(slot => <option key={slot} value={slot}>{slot}</option>)}
                    </select>
                    {validationErrors.pickupTime && <p className="text-red-500 text-xs mt-1 font-medium">{validationErrors.pickupTime}</p>}
                    <p className="text-xs mt-2 leading-relaxed" style={{color: '#a8a29e'}}>📍 {PICKUP_ADDRESS}</p>
                  </div>
                </div>
              )}
              {orderError && (
                <div className="rounded-xl p-3.5 mb-4" style={{background: '#fff1f2', border: '1px solid #fca5a5'}}>
                  <p className="text-red-700 text-sm font-medium">{orderError}</p>
                </div>
              )}
            </div>
            {/* Footer */}
            <div className="flex-shrink-0 px-6 pb-8 pt-4" style={{borderTop: '1px solid var(--yk-border, rgba(180,140,60,0.1))', background: 'var(--yk-surface, #fffdf9)'}}>
              {cartTotal < minOrderAmount && (
                <div className="tiffin-min-order-warn flex items-center gap-2 px-4 py-3 mb-3 rounded-xl">
                  <span style={{fontSize: '16px'}}>⚠️</span>
                  <span className="tiffin-min-order-warn-text text-sm font-semibold">
                    Minimum order is ${minOrderAmount.toFixed(2)} — add ${(minOrderAmount - cartTotal).toFixed(2)} more to proceed.
                  </span>
                </div>
              )}
              <button onClick={handleProceedToPayment} disabled={orderLoading || cartTotal < minOrderAmount}
                className="tiffin-checkout-btn w-full font-extrabold py-4 rounded-2xl text-base tracking-wide transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                style={{background: '#ffffff', color: '#111827', border: '1.5px solid #d1d5db', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', letterSpacing: '-0.01em'}}>
                {orderLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-t-transparent" style={{borderColor: 'var(--yk-border)', borderTopColor: 'var(--yk-text)'}}></div>
                    Creating Order…
                  </span>
                ) : showCustomerForm ? `Pay $${cartTotal.toFixed(2)} →` : 'Continue to Checkout →'}
              </button>
              <button onClick={() => { dispatch(clearCart()); setShowCartDrawer(false); }}
                className="w-full font-medium py-3 text-xs uppercase tracking-widest mt-3 transition hover:text-red-500" style={{color: 'var(--yk-text-muted, #a8a29e)'}}>Clear Cart</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sita Rosi slide-over panel ── */}
      {/* ── Custom confirm dialog ── */}
      {confirmDialog.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" style={{background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)'}}>
          <div className="relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden" style={{background: 'var(--yk-surface, #ffffff)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
            <div className="px-6 pt-6 pb-2">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{background: '#fef3c7', border: '1px solid #fde68a'}}>⚠️</div>
                <p className="text-sm font-semibold leading-relaxed" style={{color: 'var(--yk-text, #111827)'}}>{confirmDialog.message}</p>
              </div>
            </div>
            <div className="flex gap-2 px-6 pb-6">
              <button
                onClick={() => setConfirmDialog({ open: false, message: '', onConfirm: null })}
                className="flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all hover:opacity-80"
                style={{background: 'var(--yk-surface-2, #f3f4f6)', color: 'var(--yk-text-muted, #6b7280)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
                Cancel
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm transition-all hover:opacity-90"
                style={{background: 'linear-gradient(135deg,#92400e,#b45309)', color: '#ffffff', border: 'none', boxShadow: '0 4px 12px rgba(180,83,9,0.3)'}}>
                Clear &amp; Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {false && (
        <div className={`fixed inset-0 z-50 ${isMobile ? 'flex flex-col justify-end' : 'flex justify-end'}`}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSitaRosiOpen(false)} />
          {/* Panel */}
          <div className={`relative flex flex-col shadow-2xl tiffin-sita-rosi-panel ${isMobile ? 'w-full rounded-t-3xl' : 'w-full max-w-lg h-full'}`}
            style={{background: '#ffffff', maxHeight: isMobile ? '92vh' : '100vh'}} data-sr-panel>
            {/* Mobile drag handle */}
            {isMobile && <div className="flex-shrink-0 flex justify-center pt-3 pb-1"><div className="w-10 h-1 rounded-full" style={{background: 'rgba(180,130,60,0.25)'}} /></div>}
            {/* Header */}
            <div className="sr-header flex-shrink-0 px-5 py-4 flex items-center justify-between" style={{borderBottom: '1.5px solid var(--yk-border, #e5e7eb)', background: 'var(--yk-surface, #ffffff)'}}>
              <div>
                <h2 className="font-extrabold text-2xl tracking-tight sr-gold" style={{color: '#111827', letterSpacing: '-0.02em'}}>🏠 Sita Rasoi</h2>
                <p className="text-xs mt-0.5 font-medium sr-gold" style={{color: '#6b7280'}}>Kitchen Menu — Today's Lunch &amp; Dinner</p>
              </div>
              <button onClick={() => setSitaRosiOpen(false)} className="sr-close-btn w-9 h-9 rounded-full flex items-center justify-center transition-colors" style={{color: '#374151', background: '#f3f4f6', border: '1px solid #e5e7eb'}}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="sr-tab-bar flex-shrink-0 px-5 pt-4 pb-3" style={{background: 'var(--yk-surface, #ffffff)', borderBottom: '1px solid var(--yk-border, #f3f4f6)'}}>
              <div className="flex p-1 rounded-xl gap-1" style={{background: 'var(--yk-surface-2, #f4f4f5)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
              {['lunch', 'dinner'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setSitaRosiTab(tab)}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold tracking-wide transition-all sr-tab${sitaRosiTab === tab ? ' sr-tab-active' : ''}`}
                  style={sitaRosiTab === tab
                    ? {background: 'var(--yk-surface, #ffffff)', color: 'var(--yk-text, #111827)', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', border: '1px solid var(--yk-border, #e5e7eb)'}
                    : {background: 'transparent', color: 'var(--yk-text-muted, #71717a)', border: '1px solid transparent'}}
                >
                  {tab === 'lunch' ? '☀️ Lunch' : '🌙 Dinner'}
                </button>
              ))}
              </div>
            </div>

            {/* Content */}
            <div className="sr-content flex-1 overflow-y-auto px-5 pb-6" style={{background: 'var(--yk-bg, #f9fafb)'}}>
              {sitaRosiError && (
                <div className="mt-3 mb-2 px-3 py-2 rounded-lg text-xs" style={{background: '#fff1f2', border: '1px solid #fca5a5', color: '#dc2626'}}>
                  <strong>Error:</strong> {sitaRosiError}
                </div>
              )}
              {sitaRosiLoading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{borderColor: 'rgba(180,140,60,0.2)', borderTopColor: '#b45309'}} />
                  <p className="text-sm" style={{color: 'var(--yk-text-muted)'}}>Loading kitchen menu…</p>
                </div>
              ) : (() => {
                const items = sitaRosiItems[sitaRosiTab] || [];
                if (items.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-16 gap-2 text-center">
                      <span className="text-4xl">{sitaRosiTab === 'lunch' ? '☀️' : '🌙'}</span>
                      <p className="font-semibold" style={{color: 'var(--yk-text)'}}>No {sitaRosiTab} menu today</p>
                      <p className="text-xs" style={{color: 'var(--yk-text-muted)'}}>No items scheduled for today's {sitaRosiTab}</p>
                    </div>
                  );
                }
                const grouped = items.reduce((acc, item) => {
                  const cat = item.item_Category_Name || item.Item_Category_Name || 'Other';
                  if (!acc[cat]) acc[cat] = [];
                  acc[cat].push(item);
                  return acc;
                }, {});
                return Object.entries(grouped).map(([category, catItems]) => (
                  <div key={category} className="mb-4">
                    <div className="sr-category-sticky sticky top-0 py-1.5 mb-2" style={{background: 'var(--yk-bg, #f9fafb)'}}>
                      <span className="text-xs font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md sr-category" style={{background: 'var(--yk-surface-2, #f4f4f5)', color: 'var(--yk-text-muted, #52525b)', border: '1px solid var(--yk-border, #e5e7eb)', letterSpacing: '0.06em'}}>
                        {category}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {catItems.map((item, idx) => {
                        const rawUrl = item.item_Media_URL || item.Item_Media_URL || '';
                        const imgSrc = rawUrl ? `https://ykportal.yugalkunj.org/${rawUrl.replace(/^\.\.\//, '')}` : '';
                        const isPlateOrTray = (item.is_Plate_Or_Tray || item.Is_Plate_Or_Tray) === 'Y';
                        const pricePerUnit = item.pricePerUnit || item.PricePerUnit || 0;
                        const halfPlatePrice = item.half_Plate_Price || item.Half_Plate_Price || 0;
                        const fullPlatePrice = item.full_Plate_Price || item.Full_Plate_Price || 0;
                        const price = isPlateOrTray
                          ? halfPlatePrice || fullPlatePrice
                          : pricePerUnit || halfPlatePrice || fullPlatePrice || 0;
                        const cartItem = {
                          tiffinMenuItem_Id: `sr_${item.kitchen_Item_Id || item.Kitchen_Item_Id || item.menu_Item_Id || item.Menu_Item_Id || idx}`,
                          itemName: item.item_Name || item.Item_Name,
                          itemDescription: item.item_Description || item.Item_Description || '',
                          price,
                          imageUrl: imgSrc,
                          isPlateOrTray,
                          halfPlatePrice,
                          fullPlatePrice,
                        };
                        const halfId = `${cartItem.tiffinMenuItem_Id}_half`;
                        const fullId = `${cartItem.tiffinMenuItem_Id}_full`;
                        const halfCart = { ...cartItem, tiffinMenuItem_Id: halfId, price: halfPlatePrice, itemName: `${cartItem.itemName} (½ Plate)` };
                        const fullCart = { ...cartItem, tiffinMenuItem_Id: fullId, price: fullPlatePrice, itemName: `${cartItem.itemName} (Full Plate)` };
                        const qty     = getItemQuantity(cartItem.tiffinMenuItem_Id);
                        const qtyHalf = isPlateOrTray ? getItemQuantity(halfId) : 0;
                        const qtyFull = isPlateOrTray ? getItemQuantity(fullId) : 0;
                        const inCart  = isPlateOrTray ? (qtyHalf > 0 || qtyFull > 0) : qty > 0;
                        return (
                          <div key={idx} className={`sr-item-row${inCart ? ' is-in-cart' : ''} flex items-center gap-3 px-3 py-2.5 rounded-xl`} style={{background: inCart ? 'var(--yk-surface-2, #f1f5f9)' : 'var(--yk-surface, #ffffff)', border: inCart ? '1.5px solid var(--yk-border, #94a3b8)' : '1px solid var(--yk-border, #e5e7eb)', boxShadow: inCart ? '0 1px 4px rgba(0,0,0,0.08)' : '0 1px 2px rgba(0,0,0,0.04)'}}>
                            {imgSrc && (
                              <img src={imgSrc} alt={cartItem.itemName} onError={e => { e.target.style.display = 'none'; }}
                                className="w-12 h-12 rounded-lg object-cover flex-shrink-0" style={{border: '1px solid rgba(180,140,60,0.15)'}} />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold" style={{color: 'var(--yk-text)'}}>{cartItem.itemName}</p>
                              {cartItem.itemDescription && (
                                <p className="text-xs mt-0.5 line-clamp-1" style={{color: 'var(--yk-text-muted)'}}>{cartItem.itemDescription}</p>
                              )}
                              {isPlateOrTray && (halfPlatePrice > 0 || fullPlatePrice > 0) ? (
                                <p className="text-xs font-semibold mt-0.5 sr-price" style={{color: '#64748b'}}>
                                  {halfPlatePrice > 0 && `Half Plate $${halfPlatePrice.toFixed(2)}`}
                                  {halfPlatePrice > 0 && fullPlatePrice > 0 && ' · '}
                                  {fullPlatePrice > 0 && `Full $${fullPlatePrice.toFixed(2)}`}
                                </p>
                              ) : price > 0 ? (
                                <p className="text-xs font-semibold mt-0.5 sr-price" style={{color: '#64748b'}}>${price.toFixed(2)}</p>
                              ) : null}
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-1">
                              {isPlateOrTray ? (
                                <div className="flex flex-col gap-1 items-end">
                                  {halfPlatePrice > 0 && (
                                    qtyHalf === 0 ? (
                                      <button onClick={() => handleAdd(halfCart)}
                                        className="sr-add-btn font-semibold py-1 px-2 rounded-lg text-xs transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
                                        style={{background: '#ffffff', color: '#374151', border: '1px solid #d1d5db', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'}}>
                                        Half ${halfPlatePrice.toFixed(2)} +
                                      </button>
                                    ) : (
                                      <div className="flex items-center gap-0.5 rounded-lg px-0.5 py-0.5" style={{background: 'var(--yk-surface-2, #f4f4f5)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
                                        <button onClick={() => handleRemove(halfId)} className="w-6 h-6 rounded-md font-bold flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition text-sm" style={{background: '#ffffff', border: '1px solid #e5e7eb', color: '#374151'}}>−</button>
                                        <span className="text-xs font-bold w-4 text-center" style={{color: '#111827'}}>{qtyHalf}</span>
                                        <button onClick={() => handleAdd(halfCart)} className="w-6 h-6 rounded-md font-bold flex items-center justify-center transition text-sm hover:scale-105" style={{background: '#ffffff', color: '#111827', border: '1px solid #d1d5db'}}>+</button>
                                      </div>
                                    )
                                  )}
                                  {fullPlatePrice > 0 && (
                                    qtyFull === 0 ? (
                                      <button onClick={() => handleAdd(fullCart)}
                                        className="sr-add-btn font-semibold py-1 px-2 rounded-lg text-xs transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
                                        style={{background: '#ffffff', color: '#374151', border: '1px solid #d1d5db', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'}}>
                                        Full ${fullPlatePrice.toFixed(2)} +
                                      </button>
                                    ) : (
                                      <div className="flex items-center gap-0.5 rounded-lg px-0.5 py-0.5" style={{background: 'var(--yk-surface-2, #f4f4f5)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
                                        <button onClick={() => handleRemove(fullId)} className="w-6 h-6 rounded-md font-bold flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition text-sm" style={{background: '#ffffff', border: '1px solid #e5e7eb', color: '#374151'}}>−</button>
                                        <span className="text-xs font-bold w-4 text-center" style={{color: '#111827'}}>{qtyFull}</span>
                                        <button onClick={() => handleAdd(fullCart)} className="w-6 h-6 rounded-md font-bold flex items-center justify-center transition text-sm hover:scale-105" style={{background: '#ffffff', color: '#111827', border: '1px solid #d1d5db'}}>+</button>
                                      </div>
                                    )
                                  )}
                                </div>
                              ) : qty === 0 ? (
                                <>
                                <button
                                  onClick={() => setSelectedItem({ id: cartItem.tiffinMenuItem_Id, name: cartItem.itemName, description: cartItem.itemDescription || '', price: price, imageUrl: imgSrc, itemObj: cartItem })}
                                  className="tiffin-view-detail-btn w-8 h-8 rounded-full flex items-center justify-center transition hover:scale-110 flex-shrink-0"
                                  style={{background: 'var(--yk-surface-2, #f4f4f5)', border: '1px solid var(--yk-border, #e5e7eb)', color: 'var(--yk-text-muted, #78716c)'}}
                                  title="View details"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                </button>
                                <button onClick={() => handleAdd(cartItem)}
                                  className="sr-add-btn font-semibold py-1.5 px-4 rounded-lg text-xs transition-all hover:scale-105 active:scale-95"
                                  style={{background: '#ffffff', color: '#374151', border: '1px solid #d1d5db', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'}}>
                                  Add +
                                </button>
                                </>
                              ) : (
                                <div className="sr-stepper flex items-center gap-1 rounded-lg px-1 py-1" style={{background: 'var(--yk-surface-2, #f4f4f5)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
                                  <button onClick={() => handleRemove(cartItem.tiffinMenuItem_Id)}
                                    className="sr-stepper-minus sr-stepper-btn w-7 h-7 rounded-md font-bold flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition text-sm"
                                    style={{background: '#ffffff', border: '1px solid #e5e7eb', color: '#374151'}}>−</button>
                                  <span className="sr-qty text-sm font-bold w-5 text-center" style={{color: '#111827'}}>{qty}</span>
                                  <button onClick={() => handleAdd(cartItem)}
                                    className="sr-stepper-add sr-stepper-btn w-7 h-7 rounded-md font-bold flex items-center justify-center transition text-sm hover:scale-105"
                                    style={{background: '#ffffff', color: '#111827', border: '1px solid #d1d5db'}}>+</button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>

            {/* Checkout footer */}
            <div className="sr-footer flex-shrink-0 px-5 pb-6 pt-4" style={{borderTop: '1.5px solid #e5e7eb', background: 'var(--yk-surface, #ffffff)'}}>
              {cartCount > 0 ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between px-3 py-2 rounded-xl" style={{background: 'var(--yk-surface-2, #f5f5f5)', border: '1px solid var(--yk-border, rgba(0,0,0,0.08))'}}>
                    <span className="text-xs font-semibold" style={{color: 'var(--yk-text-muted, #78716c)'}}>🛒 {cartCount} item{cartCount > 1 ? 's' : ''} in cart</span>
                    <span className="text-sm font-extrabold" style={{color: 'var(--yk-text, #1c1917)'}}>${cartTotal.toFixed(2)}</span>
                  </div>
                  {cartTotal < minOrderAmount && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{background: '#fff7ed', border: '1px solid #fed7aa'}}>
                      <span style={{fontSize: '14px'}}>⚠️</span>
                      <span className="text-xs font-semibold" style={{color: '#c2410c'}}>
                        Minimum order is ${minOrderAmount.toFixed(2)}. Add ${(minOrderAmount - cartTotal).toFixed(2)} more to checkout.
                      </span>
                    </div>
                  )}
                  <button
                    onClick={() => { setSitaRosiOpen(false); setShowCartDrawer(true); }}
                    disabled={cartTotal < minOrderAmount}
                    className="sr-checkout-btn w-full py-3.5 rounded-2xl font-extrabold text-base tracking-wide transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                    style={{background: '#ffffff', color: '#111827', border: '1.5px solid #d1d5db', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', letterSpacing: '-0.01em'}}>
                    Proceed to Checkout →
                  </button>
                  <button onClick={fetchSitaRosi} disabled={sitaRosiLoading}
                    className="sr-gold w-full py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-50"
                    style={{background: 'transparent', color: '#b45309'}}>
                    {sitaRosiLoading ? 'Refreshing…' : '↻ Refresh Menu'}
                  </button>
                </div>
              ) : (
                <button onClick={fetchSitaRosi} disabled={sitaRosiLoading}
                  className="sr-gold w-full py-2.5 rounded-xl text-xs font-bold uppercase tracking-wide transition-all disabled:opacity-50"
                  style={{background: 'rgba(180,83,9,0.05)', color: '#b45309', border: '1px solid rgba(180,83,9,0.12)'}}>
                  {sitaRosiLoading ? 'Refreshing…' : '↻ Refresh Menu'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Item Detail Modal */}
      {selectedItem && (
        <div
          className="fixed inset-0 z-[10001] flex items-end sm:items-center justify-center sm:p-4"
          style={{background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)'}}
          onClick={() => setSelectedItem(null)}
        >
          <div
            className="relative rounded-t-3xl sm:rounded-3xl overflow-hidden w-full max-w-md mx-auto"
            style={{background: 'var(--yk-surface, #ffffff)', boxShadow: '0 -8px 40px rgba(0,0,0,0.18), 0 40px 80px rgba(0,0,0,0.45)'}}
            onClick={e => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={() => setSelectedItem(null)}
              className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full flex items-center justify-center transition hover:scale-110 active:scale-95"
              style={{background: 'rgba(0,0,0,0.55)', color: '#ffffff', backdropFilter: 'blur(8px)'}}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            {/* Image hero with gradient overlay */}
            <div className="relative w-full flex-shrink-0" style={{height: '256px', background: 'linear-gradient(135deg,#fdf6ee 0%,#f5e6d0 50%,#fef3c7 100%)'}}>
              {selectedItem.imageUrl
                ? <img src={selectedItem.imageUrl} alt={selectedItem.name} className="w-full h-full object-cover" style={{display:'block'}} onError={e => { e.target.style.display = 'none'; }} />
                : <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                    <span style={{fontSize:'5rem', lineHeight:1}}>🍽️</span>
                  </div>
              }
              {/* Dark gradient scrim for text legibility */}
              <div className="absolute inset-0 pointer-events-none" style={{background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.1) 55%, transparent 100%)'}} />
              {/* Name + price pinned to bottom of image */}
              <div className="absolute bottom-0 left-0 right-0 px-5 pb-4 pt-10">
                <div className="flex items-end justify-between gap-3">
                  <h3 className="text-2xl font-extrabold text-white leading-tight drop-shadow-lg flex-1">{selectedItem.name}</h3>
                  {selectedItem.price > 0 && (
                    <span className="text-2xl font-extrabold text-white drop-shadow-lg flex-shrink-0 pb-0.5">${selectedItem.price.toFixed(2)}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-5 pt-4 pb-5">
              {selectedItem.description ? (
                <p className="text-sm leading-relaxed mb-5" style={{color: 'var(--yk-text-muted, #78716c)'}}>{selectedItem.description}</p>
              ) : (
                <div className="mb-4" />
              )}

              {/* Cart Action */}
              {(() => {
                const qty = getItemQuantity(selectedItem.id);
                return qty === 0 ? (
                  <button
                    onClick={() => { handleAdd(selectedItem.itemObj); setSelectedItem(null); }}
                    className="tiffin-action-btn w-full py-3.5 rounded-2xl font-bold text-base transition-all hover:scale-[1.02] active:scale-[0.98]"
                    style={{background: '#ffffff', color: '#111827', border: '1.5px solid #d1d5db', boxShadow: '0 2px 12px rgba(0,0,0,0.08)'}}
                  >
                    + Add to Order
                  </button>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 rounded-2xl px-1.5 py-1.5" style={{background: 'var(--yk-surface-2, #f4f4f5)', border: '1px solid var(--yk-border, #e5e7eb)'}}>
                      <button onClick={() => handleRemove(selectedItem.id)}
                        className="tiffin-stepper-minus-main w-10 h-10 rounded-xl font-bold flex items-center justify-center hover:text-red-500 transition text-lg"
                        style={{background: '#ffffff', border: '1px solid #e5e7eb', color: '#374151'}}>−</button>
                      <span className="text-lg font-extrabold w-9 text-center" style={{color: 'var(--yk-text, #292524)'}}>{qty}</span>
                      <button onClick={() => handleAdd(selectedItem.itemObj)}
                        className="tiffin-stepper-add w-10 h-10 rounded-xl font-bold flex items-center justify-center transition text-lg hover:scale-105"
                        style={{background: '#ffffff', color: '#111827', border: '1px solid #d1d5db'}}>+</button>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-medium mb-0.5" style={{color: 'var(--yk-text-muted)'}}>Total</p>
                      <span className="text-2xl font-extrabold" style={{color: 'var(--yk-text, #292524)'}}>${(selectedItem.price * qty).toFixed(2)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TiffinMenu;
